// src/lib/productionFiles.js
// ───────────────────────────────────────────────────────────────────────────
// 📎 Estado de los ARCHIVOS DE PRODUCCIÓN de una plancha. PURO/testeable.
//
// Hoy no se puede saber si una plancha tiene sus archivos listos sin abrir su
// modal: en un job de 100 planchas, responder "¿qué falta para producir?" son
// 100 clics. Esto deriva esa respuesta de los campos que ya existen
// (`printFileFront`/`printFileBack` y `cutFiles.{acm|pdf|dxf|ard}`).
//
// LA REGLA SALE DE LOS DATOS, NO DE UN SUPUESTO (medido 15-ago sobre las 1301
// planchas producidas en producción):
//   · Si imprime → PRINT frente ....... 994/1000 (99.4%)
//   · Si es 2 caras → PRINT dorso ..... 11/11 fuera de J-1077 (100%); dentro de
//     J-1077 fallan 9/9, pero ese job arrastra daño de formato histórico
//   · Si lleva corte → AL MENOS UN archivo de corte ... 1280/1301 (98.4%)
// Ningún archivo de corte concreto es obligatorio: 37% de las producidas de 1
// cara no tienen ACM, 76% no tienen ARD y 26% de las de solo corte no tienen PDF.
// El patrón `PRINT+PDF` (sin ACM ni ARD) son 359 planchas producidas — el 36.6%.
// Exigir ACM+ARD habría encendido el indicador en 3 de cada 4 filas, y un
// indicador que se activa así deja de mirarse. DXF quedó FUERA de la fila: 2 de
// 1977 en toda la base (sigue disponible en el modal).
//
// CUATRO ESTADOS, no dos: la severidad depende de DÓNDE está la plancha.
//   ok       → el archivo está
//   na       → no aplica (PRINT en solo corte, DORSO en 1 cara) — atenuado, nunca rojo
//   opcional → no está, pero el grupo de corte ya se cumple con otro archivo
//   falta    → no está y es requerido
// Un `falta` en una plancha que sigue en prepress es información (ámbar: hay
// tiempo). El mismo `falta` con la plancha ya en "Listo para Producción" es un
// error accionable (rojo): alguien la avanzó sin sus archivos. Medido: hoy eso
// ocurre en 1 sola plancha de 1977, así que el rojo es una alarma real y no ruido.
// ───────────────────────────────────────────────────────────────────────────

/** ¿El slot tiene un archivo usable? Se exige `url`: un objeto sin URL no sirve. */
export const tieneArchivo = (f) => !!f && typeof f === 'object' && !!(f.url || f.downloadURL || f.path);

const imprime = (s) => !!s?.isPrinting;
const dosCaras = (s) => imprime(s) && Number(s?.printFaces) === 2;
const llevaCorte = (s) => s?.hasCut !== false;   // legado: sin el flag, se asume con corte

/** Archivo de impresión del frente (contempla el campo legado `printFile`). */
const printFrente = (s) => tieneArchivo(s?.printFileFront) || tieneArchivo(s?.printFile);

/**
 * ¿El grupo de corte está satisfecho? Basta UNO. `cutFiles` puede venir ausente
 * (129 planchas en producción) — nunca como objeto vacío, medido —, así que el
 * optional chaining cubre las dos formas sin distinguirlas.
 */
export const tieneAlgunCorte = (s) =>
  tieneArchivo(s?.cutFiles?.acm) || tieneArchivo(s?.cutFiles?.pdf) ||
  tieneArchivo(s?.cutFiles?.ard) || tieneArchivo(s?.cutFiles?.dxf);

/**
 * Estado de cada indicador de la fila. DXF no está: vive solo en el modal.
 * @param {object} sheet
 * @return {{key:string, estado:'ok'|'falta'|'opcional'|'na'}[]}
 */
export function fileSlots(sheet) {
  const corteOk = tieneAlgunCorte(sheet);
  const delGrupoCorte = (f) => {
    if (!llevaCorte(sheet)) return 'na';
    if (tieneArchivo(f)) return 'ok';
    return corteOk ? 'opcional' : 'falta';   // sin ningún archivo de corte, los tres marcan falta
  };
  return [
    { key: 'PRINT', estado: !imprime(sheet) ? 'na' : printFrente(sheet) ? 'ok' : 'falta' },
    { key: 'DORSO', estado: !dosCaras(sheet) ? 'na' : tieneArchivo(sheet?.printFileBack) ? 'ok' : 'falta' },
    { key: 'ACM', estado: delGrupoCorte(sheet?.cutFiles?.acm) },
    { key: 'PDF', estado: delGrupoCorte(sheet?.cutFiles?.pdf) },
    { key: 'ARD', estado: delGrupoCorte(sheet?.cutFiles?.ard) },
  ];
}

/** ¿Cumple las TRES condiciones requeridas? (ningún slot en `falta`). */
export function isReadyForProduction(sheet) {
  return !fileSlots(sheet).some((s) => s.estado === 'falta');
}

/**
 * Severidad de la fila:
 *   'listo'  → no falta nada requerido
 *   'error'  → falta algo requerido Y la plancha ya está en "Listo para Producción"
 *   'falta'  → falta algo requerido pero sigue en prepress (todavía hay tiempo)
 */
export function readinessLevel(sheet) {
  if (isReadyForProduction(sheet)) return 'listo';
  return sheet?.status === 'Listo para Producción' ? 'error' : 'falta';
}

/** Opciones del filtro de archivos de la tabla de Prepress. */
export const FILE_FILTERS = ['Todas', 'Incompletas', 'En rojo', 'Completas'];

/**
 * ¿La plancha entra en la opción elegida? OJO: "En rojo" es un SUBCONJUNTO de
 * "Incompletas" (a las dos les falta un requerido; la roja además ya está en
 * "Listo para Producción"), así que los conteos NO suman el total.
 * @param {object} sheet
 * @param {string} filtro  una de FILE_FILTERS
 * @return {boolean}
 */
export function matchesFileFilter(sheet, filtro) {
  if (!filtro || filtro === 'Todas') return true;
  const nivel = readinessLevel(sheet);
  if (filtro === 'Completas') return nivel === 'listo';
  if (filtro === 'En rojo') return nivel === 'error';
  if (filtro === 'Incompletas') return nivel !== 'listo';
  return true;
}

/**
 * Conteo por categoría. Se calcula sobre las planchas que YA pasaron los demás
 * filtros pero ANTES de aplicar el de archivos: si se calculara después, elegir
 * "Incompletas" derrumbaría el resto a 0 y el conteo dejaría de informar.
 * @param {object[]} sheets
 * @return {{todas:number, incompletas:number, rojo:number, completas:number}}
 */
export function tallyReadiness(sheets = []) {
  const t = { todas: 0, incompletas: 0, rojo: 0, completas: 0 };
  (sheets || []).forEach((s) => {
    t.todas++;
    const nivel = readinessLevel(s);
    if (nivel === 'listo') t.completas++;
    else {
      t.incompletas++;                    // incluye las rojas, a propósito
      if (nivel === 'error') t.rojo++;
    }
  });
  return t;
}
